import { useEffect, useMemo, useRef, Suspense } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Grid, Sparkles } from "@react-three/drei";
import * as THREE from "three";

const BLUE = "#3565AE";
const EYE = "#5AD7FF";
const WHITE = "#EEF2F7";
const RED = "#E5484D";
const GREEN = "#3FA34D";
const ORANGE = "#F58220";
const PARTICLES = 90;
const SLEEP_AFTER = 60;

// tempi della fusione: avvicinamento, sfera unica, separazione (rallentati per godersi l'effetto)
const MERGE_T = 1.8;
const HOLD_T = 10;
const SPLIT_T = 1.8;
const FUSE_TOTAL = MERGE_T + HOLD_T + SPLIT_T;

const ORB_BASE = { x: 0.63, y: 0.42, z: 0.3 };
const ORB_FUSED = { x: 0, y: 0.4, z: 0.5 };
const ARM_BASE = { x: 0.3, y: 0.42, z: 0.05 };
const ARM_FUSED = { x: 0.14, y: 0.34, z: 0.3 };

const easeOut = (k) => 1 - Math.pow(1 - k, 3);
const easeInOut = (k) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

// --- suono "magico" sintetizzato con Web Audio API (nessun file esterno) ---
let audioCtx = null;
function getAudioCtx() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

function tone(ctx, freq, start, dur, type, vol) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(vol, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.05);
}

function sparkle(ctx, start) {
  const dur = 0.35;
  const buffer = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 2);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 5000;
  const g = ctx.createGain();
  g.gain.value = 0.12;
  src.connect(hp).connect(g).connect(ctx.destination);
  src.start(start);
}

// arpeggio ascendente scintillante + impatto morbido alla fusione
function playMagicFusion() {
  try {
    const ctx = getAudioCtx();
    const t0 = ctx.currentTime;
    const notes = [1318.5, 1568, 1975.5, 2637, 3136]; // E6 G6 B6 E7 G7
    notes.forEach((f, i) => tone(ctx, f, t0 + i * 0.06, 0.5, "triangle", 0.14));
    tone(ctx, 4186, t0 + 0.34, 0.7, "sine", 0.09); // campanellino finale
    tone(ctx, 180, t0, 0.22, "sine", 0.11); // "wump" di impatto
    tone(ctx, 90, t0, 0.3, "sine", 0.09);
    sparkle(ctx, t0 + 0.05);
    sparkle(ctx, t0 + 0.3);
  } catch {}
}

// arpeggio discendente delicato alla separazione
function playMagicSplit() {
  try {
    const ctx = getAudioCtx();
    const t0 = ctx.currentTime;
    const notes = [2637, 1975.5, 1568, 1318.5];
    notes.forEach((f, i) => tone(ctx, f, t0 + i * 0.05, 0.35, "triangle", 0.09));
    sparkle(ctx, t0);
  } catch {}
}

function LogoCircle({ position, radius = 0.13 }) {
  const texture = useLoader(THREE.TextureLoader, "/logo.png");
  texture.colorSpace = THREE.SRGBColorSpace;
  return (
    <mesh position={position}>
      <circleGeometry args={[radius, 48]} />
      <meshStandardMaterial map={texture} roughness={0.4} metalness={0.05} />
    </mesh>
  );
}

function MiniVan() {
  return (
    <group scale={0.9}>
      {/* cassone */}
      <mesh position={[-0.03, 0.02, 0]}>
        <boxGeometry args={[0.24, 0.13, 0.13]} />
        <meshStandardMaterial color={WHITE} roughness={0.35} metalness={0.1} />
      </mesh>
      {/* cabina */}
      <mesh position={[0.11, -0.005, 0]}>
        <boxGeometry args={[0.08, 0.08, 0.12]} />
        <meshStandardMaterial color={WHITE} roughness={0.35} metalness={0.1} />
      </mesh>
      {/* parabrezza */}
      <mesh position={[0.145, 0.01, 0]}>
        <boxGeometry args={[0.015, 0.05, 0.1]} />
        <meshStandardMaterial color="#9FD8FF" roughness={0.1} metalness={0.3} />
      </mesh>
      {/* striscia arancione */}
      <mesh position={[-0.03, -0.02, 0]}>
        <boxGeometry args={[0.25, 0.03, 0.135]} />
        <meshStandardMaterial color={ORANGE} emissive={ORANGE} emissiveIntensity={0.35} roughness={0.4} />
      </mesh>
      {/* ruote */}
      {[
        [-0.1, 0.055],
        [0.08, 0.055],
        [-0.1, -0.055],
        [0.08, -0.055],
      ].map(([wx, wz], i) => (
        <mesh key={i} position={[wx, -0.06, wz]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.035, 0.035, 0.02, 16]} />
          <meshStandardMaterial color="#1B2430" roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

// esplosione di particelle (al momento della fusione e della separazione)
function Burst({ clickAt, lastT, offset = 0, color = ORANGE }) {
  const points = useRef();
  const mat = useRef();
  const positions = useMemo(() => new Float32Array(PARTICLES * 3), []);
  const velocities = useMemo(() => {
    const v = new Float32Array(PARTICLES * 3);
    for (let i = 0; i < PARTICLES; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const s = 1.6 + Math.random() * 2.8;
      v[i * 3] = Math.sin(phi) * Math.cos(theta) * s;
      v[i * 3 + 1] = Math.cos(phi) * s;
      v[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * s;
    }
    return v;
  }, []);

  useFrame(() => {
    if (!points.current || !mat.current) return;
    const bt = lastT.current - (clickAt.current + offset);
    if (bt >= 0 && bt < 1) {
      for (let i = 0; i < PARTICLES; i++) {
        positions[i * 3] = ORB_FUSED.x + velocities[i * 3] * bt;
        positions[i * 3 + 1] = ORB_FUSED.y + velocities[i * 3 + 1] * bt - bt * bt * 1.4;
        positions[i * 3 + 2] = ORB_FUSED.z + velocities[i * 3 + 2] * bt;
      }
      points.current.geometry.attributes.position.needsUpdate = true;
      mat.current.opacity = 1 - bt;
    } else if (mat.current.opacity !== 0) {
      mat.current.opacity = 0;
    }
  });

  return (
    <points ref={points} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        ref={mat}
        size={0.08}
        color={color}
        transparent
        opacity={0}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        sizeAttenuation
      />
    </points>
  );
}

// alone energetico che risucchia le particelle verso il centro durante la fusione
function Implode({ fuseAt, lastT }) {
  const points = useRef();
  const mat = useRef();
  const positions = useMemo(() => new Float32Array(PARTICLES * 3), []);
  const starts = useMemo(() => {
    const v = new Float32Array(PARTICLES * 3);
    for (let i = 0; i < PARTICLES; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 1.1 + Math.random() * 0.9;
      v[i * 3] = Math.sin(phi) * Math.cos(theta) * r;
      v[i * 3 + 1] = Math.cos(phi) * r * 0.8;
      v[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * r;
    }
    return v;
  }, []);

  useFrame(() => {
    if (!points.current || !mat.current) return;
    const ft = lastT.current - fuseAt.current;
    if (ft >= 0 && ft < MERGE_T) {
      const e = easeInOut(ft / MERGE_T);
      for (let i = 0; i < PARTICLES; i++) {
        positions[i * 3] = ORB_FUSED.x + starts[i * 3] * (1 - e);
        positions[i * 3 + 1] = ORB_FUSED.y + starts[i * 3 + 1] * (1 - e);
        positions[i * 3 + 2] = ORB_FUSED.z + starts[i * 3 + 2] * (1 - e);
      }
      points.current.geometry.attributes.position.needsUpdate = true;
      mat.current.opacity = 0.25 + e * 0.75;
    } else if (mat.current.opacity !== 0) {
      mat.current.opacity = 0;
    }
  });

  return (
    <points ref={points} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        ref={mat}
        size={0.07}
        color={EYE}
        transparent
        opacity={0}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        sizeAttenuation
      />
    </points>
  );
}

// anello che si espande al momento della fusione e della separazione
function FusionRing({ fuseAt, lastT }) {
  const ring = useRef();
  useFrame(() => {
    if (!ring.current) return;
    const t = lastT.current;
    const moments = [fuseAt.current + MERGE_T, fuseAt.current + MERGE_T + HOLD_T];
    let d = -1;
    for (const m0 of moments) {
      const dd = t - m0;
      if (dd >= 0 && dd < 0.7) d = dd;
    }
    if (d >= 0) {
      const k = d / 0.7;
      ring.current.scale.setScalar(0.2 + easeOut(k) * 1.7);
      ring.current.material.opacity = (1 - k) * 0.9;
      ring.current.visible = true;
    } else {
      ring.current.visible = false;
    }
  });
  return (
    <mesh ref={ring} position={[ORB_FUSED.x, ORB_FUSED.y, ORB_FUSED.z]} visible={false}>
      <torusGeometry args={[0.5, 0.03, 12, 48]} />
      <meshBasicMaterial color={ORANGE} transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} />
    </mesh>
  );
}

// lampo di luce al momento della fusione e della separazione
function FlashLight({ fuseAt, lastT }) {
  const light = useRef();
  useFrame(() => {
    if (!light.current) return;
    const t = lastT.current;
    const moments = [fuseAt.current + MERGE_T, fuseAt.current + MERGE_T + HOLD_T];
    let intensity = 0;
    for (const m0 of moments) {
      const d = t - m0;
      if (d >= 0 && d < 0.5) intensity = Math.max(intensity, (1 - d / 0.5) * 140);
    }
    light.current.intensity = intensity;
  });
  return <pointLight ref={light} position={[0, 0.7, 1.4]} intensity={0} color="#FFC46B" distance={7} />;
}

function Mascot({ onInteract, onSleepChange, onFusionChange, muted }) {
  const root = useRef();
  const head = useRef();
  const eyeL = useRef();
  const eyeR = useRef();
  const mouth = useRef();
  const van = useRef();
  const fused = useRef();
  const flames = useRef([]);
  const flameInners = useRef([]);
  const leafL = useRef();
  const leafR = useRef();
  const cape = useRef();
  const orbs = useRef([]);
  const arms = useRef([]);
  const fuseAt = useRef(-100);
  const lastT = useRef(0);
  const lastActivity = useRef(0);
  const prevPointer = useRef({ x: 0, y: 0 });
  const pendingWake = useRef(false);
  const sleepF = useRef(0);
  const wasSleeping = useRef(false);
  const wasFused = useRef(false);
  const mutedRef = useRef(false);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);
  const nextBlink = useRef(2.5);
  const blinkUntil = useRef(-1);
  const cEye = useMemo(() => new THREE.Color(EYE), []);
  const cOrange = useMemo(() => new THREE.Color(ORANGE), []);

  useEffect(() => {
    const wake = () => {
      pendingWake.current = true;
    };
    window.addEventListener("pointermove", wake);
    return () => window.removeEventListener("pointermove", wake);
  }, []);

  const handleClick = (e) => {
    e.stopPropagation();
    lastActivity.current = lastT.current;
    // ignora il click se la fusione è già in corso
    if (lastT.current - fuseAt.current < FUSE_TOTAL) return;
    getAudioCtx(); // sblocca l'audio dentro il gesto del click
    fuseAt.current = lastT.current;
    onInteract?.();
  };

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    lastT.current = t;
    const p = state.pointer;

    if (
      pendingWake.current ||
      Math.abs(p.x - prevPointer.current.x) > 0.004 ||
      Math.abs(p.y - prevPointer.current.y) > 0.004
    ) {
      lastActivity.current = t;
      prevPointer.current.x = p.x;
      prevPointer.current.y = p.y;
      pendingWake.current = false;
    }

    const sleepTarget = t - lastActivity.current > SLEEP_AFTER ? 1 : 0;
    sleepF.current = THREE.MathUtils.lerp(sleepF.current, sleepTarget, 0.015);
    const s = sleepF.current;
    const awake = 1 - s;
    const sleeping = s > 0.6;
    if (sleeping !== wasSleeping.current) {
      wasSleeping.current = sleeping;
      onSleepChange?.(sleeping);
    }

    // ammiccamento
    if (t > nextBlink.current) {
      blinkUntil.current = t + 0.14;
      nextBlink.current = t + 2.4 + Math.random() * 3.2;
    }
    const eyeTarget = t < blinkUntil.current || sleeping ? 0.08 : 1;
    eyeL.current.scale.y = THREE.MathUtils.lerp(eyeL.current.scale.y, eyeTarget, 0.4);
    eyeR.current.scale.y = eyeL.current.scale.y;

    // gli occhi seguono il cursore
    eyeL.current.position.x = -0.22 + p.x * 0.05 * awake;
    eyeL.current.position.y = 0.05 + p.y * 0.04 * awake;
    eyeR.current.position.x = 0.22 + p.x * 0.05 * awake;
    eyeR.current.position.y = 0.05 + p.y * 0.04 * awake;

    // testa che segue il cursore / si china nel sonno
    head.current.rotation.y = THREE.MathUtils.lerp(head.current.rotation.y, p.x * 0.55 * awake, 0.07);
    head.current.rotation.x = THREE.MathUtils.lerp(
      head.current.rotation.x,
      -p.y * 0.28 * awake + s * 0.42,
      0.07
    );

    // --- fusione delle sfere ---
    const ft = t - fuseAt.current;
    const fuseActive = ft >= 0 && ft < FUSE_TOTAL;
    let m = 0;
    let mergePulse = 0;
    if (ft >= 0 && ft < MERGE_T) {
      m = easeInOut(ft / MERGE_T);
      mergePulse = Math.sin((ft / MERGE_T) * Math.PI);
    } else if (ft >= MERGE_T && ft < MERGE_T + HOLD_T) {
      m = 1;
      mergePulse = 0.45 + Math.sin(t * 3) * 0.2;
    } else if (ft >= MERGE_T + HOLD_T && ft < FUSE_TOTAL) {
      m = 1 - easeInOut((ft - MERGE_T - HOLD_T) / SPLIT_T);
      mergePulse = Math.sin(((ft - MERGE_T - HOLD_T) / SPLIT_T) * Math.PI);
    }

    // notifica quando la fusione è completa (sfera unica attiva) e quando termina
    const fusedNow = ft >= MERGE_T && ft < MERGE_T + HOLD_T;
    if (fusedNow !== wasFused.current) {
      wasFused.current = fusedNow;
      onFusionChange?.(fusedNow);
      if (!mutedRef.current) {
        if (fusedNow) playMagicFusion();
        else playMagicSplit();
      }
    }

    // sfere laterali che si avvicinano e scompaiono nella sfera unica
    for (let idx = 0; idx < 2; idx++) {
      const side = idx === 0 ? -1 : 1;
      const orb = orbs.current[idx];
      if (orb) {
        orb.position.set(
          side * ORB_BASE.x * (1 - m),
          ORB_BASE.y + (ORB_FUSED.y - ORB_BASE.y) * m,
          ORB_BASE.z + (ORB_FUSED.z - ORB_BASE.z) * m
        );
        orb.scale.setScalar(Math.max(0.001, 1 - m));
      }
      const arm = arms.current[idx];
      if (arm) {
        arm.position.set(
          side * (ARM_BASE.x + (ARM_FUSED.x - ARM_BASE.x) * m),
          ARM_BASE.y + (ARM_FUSED.y - ARM_BASE.y) * m,
          ARM_BASE.z + (ARM_FUSED.z - ARM_BASE.z) * m
        );
      }
    }

    // sfera unica con il logo
    if (fused.current) {
      const breathe = ft >= MERGE_T && ft < MERGE_T + HOLD_T ? 1 + Math.sin(t * 2.5) * 0.04 : 1;
      const pop = ft >= 0 && ft < MERGE_T ? easeOut(ft / MERGE_T) : m;
      fused.current.scale.setScalar(Math.max(0.001, pop * breathe));
      fused.current.rotation.y = fuseActive ? Math.sin(t * 0.8) * 0.45 : 0;
      fused.current.visible = m > 0.002;
    }

    // il sorriso si allarga durante la fusione
    if (mouth.current) {
      mouth.current.scale.x = 1 + mergePulse * 0.45;
      mouth.current.material.emissiveIntensity = 1.8 + mergePulse * 2.4;
    }

    // galleggiamento + saltello durante la fusione
    let y = Math.sin(t * (1.5 - 0.8 * s)) * (0.1 - 0.05 * s) - s * 0.22 + mergePulse * 0.12;
    let flameBoost = mergePulse;
    if (fuseActive) {
      const pulse = Math.min(1, mergePulse + 0.25);
      eyeL.current.material.color.copy(cEye).lerp(cOrange, pulse);
      eyeL.current.material.emissive.copy(cEye).lerp(cOrange, pulse);
      eyeR.current.material.color.copy(cEye).lerp(cOrange, pulse);
      eyeR.current.material.emissive.copy(cEye).lerp(cOrange, pulse);
      eyeL.current.material.emissiveIntensity = 2.6 + pulse * 2.5;
      eyeR.current.material.emissiveIntensity = 2.6 + pulse * 2.5;
    } else {
      eyeL.current.material.color.copy(cEye);
      eyeL.current.material.emissive.copy(cEye);
      eyeR.current.material.color.copy(cEye);
      eyeR.current.material.emissive.copy(cEye);
      const eyeGlow = (2.6 + Math.sin(t * 2) * 0.3) * (1 - 0.92 * s);
      eyeL.current.material.emissiveIntensity = eyeGlow;
      eyeR.current.material.emissiveIntensity = eyeGlow;
    }
    root.current.rotation.y = 0;
    root.current.rotation.x = THREE.MathUtils.lerp(
      root.current.rotation.x,
      -p.y * 0.08 * awake + s * 0.08,
      0.08
    );
    root.current.position.y = y;

    // fiamme dei razzi: tremolio, si spengono nel sonno, fiammata durante la fusione
    flames.current.forEach((f, i) => {
      if (!f) return;
      const flicker = 0.9 + Math.sin(t * 13 + i * 1.7) * 0.15;
      const fs = (0.06 + 0.94 * awake) * flicker * (1 + flameBoost * 0.9);
      f.scale.set(1, fs, 1);
      f.material.emissiveIntensity = (2.2 + flameBoost * 2) * (0.1 + 0.9 * awake);
    });
    flameInners.current.forEach((f, i) => {
      if (!f) return;
      const flicker = 0.9 + Math.sin(t * 16 + i * 2.1) * 0.18;
      const fs = (0.06 + 0.94 * awake) * flicker * (1 + flameBoost * 0.9);
      f.scale.set(1, fs, 1);
      f.material.emissiveIntensity = (2.6 + flameBoost * 2) * (0.1 + 0.9 * awake);
    });

    // mantello, germoglio e furgoncino che si muovono dolcemente
    cape.current.rotation.z = Math.sin(t * 1.8) * 0.05 * awake;
    leafL.current.rotation.z = 0.6 + Math.sin(t * 1.7) * 0.12 * awake + s * 0.6;
    leafR.current.rotation.z = -0.6 - Math.sin(t * 1.7 + 1) * 0.12 * awake - s * 0.6;
    if (van.current) van.current.rotation.y = Math.sin(t * 0.9) * 0.55;
  });

  return (
    <group
      ref={root}
      onPointerDown={handleClick}
      onPointerOver={() => (document.body.style.cursor = "pointer")}
      onPointerOut={() => (document.body.style.cursor = "auto")}
    >
      {/* testa */}
      <group ref={head} position={[0, 1.0, 0]}>
        <mesh scale={[1, 0.95, 0.92]}>
          <sphereGeometry args={[0.62, 48, 48]} />
          <meshStandardMaterial color={WHITE} roughness={0.3} metalness={0.15} />
        </mesh>
        {/* visiera nera */}
        <mesh position={[0, 0, 0.3]} scale={[0.85, 0.74, 0.8]}>
          <sphereGeometry args={[0.52, 48, 48]} />
          <meshStandardMaterial color="#0B1526" roughness={0.15} metalness={0.4} />
        </mesh>
        {/* occhi luminosi */}
        <mesh ref={eyeL} position={[-0.22, 0.05, 0.66]} scale={[1, 1.15, 0.45]}>
          <sphereGeometry args={[0.11, 24, 24]} />
          <meshStandardMaterial color={EYE} emissive={EYE} emissiveIntensity={2.6} roughness={0.2} />
        </mesh>
        <mesh ref={eyeR} position={[0.22, 0.05, 0.66]} scale={[1, 1.15, 0.45]}>
          <sphereGeometry args={[0.11, 24, 24]} />
          <meshStandardMaterial color={EYE} emissive={EYE} emissiveIntensity={2.6} roughness={0.2} />
        </mesh>
        {/* bocca sorridente */}
        <mesh ref={mouth} position={[0, -0.17, 0.63]} rotation={[-0.15, 0, Math.PI]}>
          <torusGeometry args={[0.15, 0.022, 12, 40, Math.PI]} />
          <meshStandardMaterial color={EYE} emissive={EYE} emissiveIntensity={1.8} roughness={0.2} />
        </mesh>
        {/* antenne rosse */}
        {[-1, 1].map((side) => (
          <group key={side} position={[side * 0.3, 0.58, 0]} rotation={[0, 0, -side * 0.18]}>
            <mesh position={[0, 0.12, 0]}>
              <cylinderGeometry args={[0.02, 0.02, 0.24, 8]} />
              <meshStandardMaterial color="#8A94A6" metalness={0.6} roughness={0.35} />
            </mesh>
            <mesh position={[0, 0.3, 0]}>
              <capsuleGeometry args={[0.045, 0.1, 8, 16]} />
              <meshStandardMaterial color={RED} emissive={RED} emissiveIntensity={0.6} roughness={0.35} />
            </mesh>
          </group>
        ))}
        {/* cuffie */}
        {[-1, 1].map((side) => (
          <group key={side} position={[side * 0.6, 0.02, 0]} rotation={[0, 0, Math.PI / 2]}>
            <mesh>
              <cylinderGeometry args={[0.17, 0.17, 0.1, 24]} />
              <meshStandardMaterial color={RED} roughness={0.35} />
            </mesh>
            <mesh position={[0, side * 0.06, 0]}>
              <cylinderGeometry args={[0.09, 0.09, 0.05, 24]} />
              <meshStandardMaterial color={ORANGE} emissive={ORANGE} emissiveIntensity={0.5} roughness={0.35} />
            </mesh>
          </group>
        ))}
      </group>

      {/* corpo */}
      <mesh position={[0, 0.15, 0]}>
        <capsuleGeometry args={[0.26, 0.35, 8, 24]} />
        <meshStandardMaterial color={WHITE} roughness={0.35} metalness={0.1} />
      </mesh>

      {/* logo sul torace */}
      <Suspense fallback={null}>
        <LogoCircle position={[0, 0.18, 0.262]} radius={0.16} />
      </Suspense>

      {/* mantello rosso */}
      <mesh ref={cape} position={[0, -0.05, -0.22]} scale={[1, 1, 0.42]}>
        <coneGeometry args={[0.55, 1.05, 24]} />
        <meshStandardMaterial color={RED} roughness={0.5} />
      </mesh>

      {/* braccia che sorreggono le sfere */}
      {[-1, 1].map((side, idx) => (
        <group key={side} ref={(el) => (arms.current[idx] = el)} position={[side * 0.3, 0.42, 0.05]}>
          <mesh position={[side * 0.16, -0.1, 0.12]} rotation={[0.5, 0, side * 0.6]}>
            <capsuleGeometry args={[0.085, 0.32, 8, 16]} />
            <meshStandardMaterial color={WHITE} roughness={0.35} metalness={0.1} />
          </mesh>
          <mesh position={[side * 0.33, -0.18, 0.24]}>
            <sphereGeometry args={[0.1, 16, 16]} />
            <meshStandardMaterial color="#8A94A6" metalness={0.5} roughness={0.35} />
          </mesh>
        </group>
      ))}

      {/* sfera sinistra con piantina / sfera destra con furgoncino */}
      {[-1, 1].map((side, idx) => (
        <group key={side} ref={(el) => (orbs.current[idx] = el)} position={[side * 0.63, 0.42, 0.3]}>
          <mesh>
            <sphereGeometry args={[0.26, 32, 32]} />
            <meshPhysicalMaterial
              color="#ffffff"
              transparent
              opacity={0.22}
              roughness={0.08}
              metalness={0}
              clearcoat={1}
            />
          </mesh>
          {side === -1 && (
            <group>
              <mesh position={[0, -0.15, 0]} scale={[1, 0.5, 1]}>
                <sphereGeometry args={[0.12, 16, 16]} />
                <meshStandardMaterial color="#3E2A1E" roughness={0.9} />
              </mesh>
              <mesh position={[0, 0.0, 0]}>
                <cylinderGeometry args={[0.015, 0.015, 0.18, 8]} />
                <meshStandardMaterial color={GREEN} roughness={0.5} />
              </mesh>
              <mesh ref={leafL} position={[-0.08, 0.1, 0]} rotation={[0, 0, 0.6]} scale={[1, 0.35, 0.5]}>
                <sphereGeometry args={[0.1, 16, 16]} />
                <meshStandardMaterial color={GREEN} emissive={GREEN} emissiveIntensity={0.25} roughness={0.45} />
              </mesh>
              <mesh ref={leafR} position={[0.08, 0.12, 0]} rotation={[0, 0, -0.6]} scale={[1, 0.35, 0.5]}>
                <sphereGeometry args={[0.1, 16, 16]} />
                <meshStandardMaterial color={GREEN} emissive={GREEN} emissiveIntensity={0.25} roughness={0.45} />
              </mesh>
            </group>
          )}
          {side === 1 && (
            <group ref={van}>
              <MiniVan />
            </group>
          )}
        </group>
      ))}

      {/* sfera unica della fusione con il logo */}
      <group ref={fused} position={[ORB_FUSED.x, ORB_FUSED.y, ORB_FUSED.z]} visible={false}>
        <mesh>
          <sphereGeometry args={[0.42, 32, 32]} />
          <meshPhysicalMaterial
            color="#DCE9FA"
            transparent
            opacity={0.38}
            roughness={0.06}
            metalness={0}
            clearcoat={1}
          />
        </mesh>
        <mesh>
          <sphereGeometry args={[0.52, 24, 24]} />
          <meshBasicMaterial color={ORANGE} transparent opacity={0.16} depthWrite={false} blending={THREE.AdditiveBlending} />
        </mesh>
        <Suspense fallback={null}>
          <LogoCircle position={[0, 0, 0.18]} radius={0.32} />
        </Suspense>
      </group>

      {/* stivali con razzi */}
      {[-1, 1].map((side, idx) => (
        <group key={side} position={[side * 0.17, -0.34, 0]}>
          <mesh>
            <capsuleGeometry args={[0.1, 0.12, 8, 16]} />
            <meshStandardMaterial color={RED} roughness={0.4} />
          </mesh>
          <mesh
            ref={(el) => (flames.current[idx] = el)}
            position={[0, -0.3, 0]}
            rotation={[Math.PI, 0, 0]}
          >
            <coneGeometry args={[0.085, 0.34, 16]} />
            <meshStandardMaterial color={ORANGE} emissive={ORANGE} emissiveIntensity={2.2} transparent opacity={0.9} />
          </mesh>
          <mesh
            ref={(el) => (flameInners.current[idx] = el)}
            position={[0, -0.26, 0]}
            rotation={[Math.PI, 0, 0]}
          >
            <coneGeometry args={[0.045, 0.22, 12]} />
            <meshStandardMaterial color="#FFD166" emissive="#FFD166" emissiveIntensity={2.6} transparent opacity={0.95} />
          </mesh>
        </group>
      ))}

      {/* effetti della fusione */}
      <Implode fuseAt={fuseAt} lastT={lastT} />
      <FusionRing fuseAt={fuseAt} lastT={lastT} />
      <FlashLight fuseAt={fuseAt} lastT={lastT} />
      <Burst clickAt={fuseAt} lastT={lastT} offset={MERGE_T} color={ORANGE} />
      <Burst clickAt={fuseAt} lastT={lastT} offset={MERGE_T + HOLD_T} color={EYE} />
    </group>
  );
}

function CameraRig() {
  useFrame((state) => {
    state.camera.position.x = THREE.MathUtils.lerp(state.camera.position.x, state.pointer.x * 0.6, 0.05);
    state.camera.position.y = THREE.MathUtils.lerp(state.camera.position.y, 0.75 + state.pointer.y * 0.3, 0.05);
    state.camera.lookAt(0, 0.3, 0);
  });
  return null;
}

export default function RobotHero3D({ onInteract, onSleepChange, onFusionChange, muted }) {
  return (
    <div data-testid="hero-3d-canvas" className="absolute inset-0">
      <Canvas camera={{ position: [0, 0.75, 6.6], fov: 42 }} dpr={[1, 1.5]} gl={{ antialias: true, alpha: true }}>
        <fog attach="fog" args={["#F5F8FD", 9, 18]} />
        <ambientLight intensity={0.7} />
        <pointLight position={[4, 4, 4]} intensity={60} color={BLUE} />
        <pointLight position={[-4, -1, 3]} intensity={40} color={ORANGE} />
        <spotLight position={[0, 7, 3]} angle={0.5} penumbra={1} intensity={90} color="#ffffff" />
        <CameraRig />
        <Mascot onInteract={onInteract} onSleepChange={onSleepChange} onFusionChange={onFusionChange} muted={muted} />
        <Sparkles count={70} scale={[9, 6, 4]} size={2} speed={0.3} color={BLUE} opacity={0.5} position={[0, 1, 0]} />
        <Grid
          position={[0, -1.8, 0]}
          args={[24, 24]}
          cellSize={0.6}
          cellColor="#C9D8EE"
          sectionSize={3}
          sectionColor={BLUE}
          fadeDistance={15}
          fadeStrength={2.5}
          infiniteGrid
        />
      </Canvas>
    </div>
  );
}
